import React, {
  useEffect,
  useRef,
  useState,
  useMemo,
  useCallback,
} from "react";
import { Star, User, Trophy, Award, Medal } from "lucide-react";
import * as Chart from "chart.js";
import { Link } from "react-router-dom";

// Register Chart.js components
Chart.Chart.register(
  Chart.CategoryScale,
  Chart.LinearScale,
  Chart.BarElement,
  Chart.Title,
  Chart.Tooltip,
  Chart.Legend,
);

// Type definitions
interface UsersData {
  id: string;
  fullName: string;
  profilePicture: string | null;
  bookingsCount: string;
  ridesCount: string;
  totalPaid: number;
  rating: string;
}

interface BarColorConfig {
  background: string;
  solid: string;
}

interface AvatarPosition {
  left: number;
  top: number;
  canvasTop: number;
  user: UsersData;
  index: number;
}

interface TopUsersChartProps {
  users: UsersData[]; // Accept both formats
  barColor: string;
  type: string;
}

interface TooltipContext {
  dataIndex: number;
}

interface ChartTooltipItem {
  dataIndex: number;
}

const TopUsersChart: React.FC<TopUsersChartProps> = ({
  users,
  barColor,
  type,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const chartRef = useRef<Chart.Chart | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [avatarPositions, setAvatarPositions] = useState<AvatarPosition[]>([]);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 640);
    };

    handleResize();
    window.addEventListener("resize", handleResize);

    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const safeUsers = useMemo(() => {
    const getProcessedUsers = (
      data: { users: UsersData[] } | UsersData[],
    ): UsersData[] => {
      if (!data) return [];

      let rawUsers: UsersData[] = [];

      // Extract the users array
      if (
        typeof data === "object" &&
        "users" in data &&
        Array.isArray(data.users)
      ) {
        rawUsers = data.users;
      } else if (Array.isArray(data)) {
        rawUsers = data;
      }

      // Transform the data to match our interface
      return rawUsers.map(
        (user: UsersData): UsersData => ({
          id: user.id || "",
          fullName: user.fullName || "Unknown User",
          profilePicture: user.profilePicture || "",
          bookingsCount: user.bookingsCount || "0",
          ridesCount: user.ridesCount || "0",
          totalPaid: user.totalPaid || 0,
          rating: user.rating || "0",
        }),
      );
    };

    return getProcessedUsers(users);
  }, [users]);

  const barColors = useMemo(() => {
    const getBarColors = (users: UsersData[]): BarColorConfig[] => {
      return users.map((_, index) => {
        if (index === 0)
          return {
            background: "linear-gradient(135deg, #617B61 0%, #427A43 100%)",
            solid: "#617B61",
          };
        if (index === 1)
          return {
            background: "linear-gradient(135deg, #C0BCA1 0%, #C0B87A 100%)",
            solid: "#C0BCA1",
          };
        if (index === 2)
          return {
            background: "linear-gradient(135deg, #F1C242 0%, #F2E3BB 100%)",
            solid: "#F1C242",
          };
        return {
          background: `linear-gradient(135deg, ${barColor} 0%, ${barColor}CC 100%)`,
          solid: barColor,
        };
      });
    };

    return getBarColors(safeUsers);
  }, [safeUsers, barColor]);

  const getRankIcon = (index: number): JSX.Element | null => {
    if (index === 0)
      return <Trophy className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-500" />;
    if (index === 1)
      return <Award className="w-3 h-3 sm:w-4 sm:h-4 text-gray-400" />;
    if (index === 2)
      return <Medal className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-700" />;
    return null;
  };

  const updateAvatarPositions = useCallback((): void => {
    if (!chartRef.current || !containerRef.current) return;

    const chart = chartRef.current;
    const positions: AvatarPosition[] = [];

    safeUsers.forEach((user, index) => {
      const meta = chart.getDatasetMeta(0);
      const bar = meta.data[index];

      if (bar) {
        const canvasRect = chart.canvas.getBoundingClientRect();
        const containerRect = containerRef.current!.getBoundingClientRect();

        positions.push({
          left: bar.x - (isMobile ? 20 : 30),
          top: bar.y - (isMobile ? 50 : 40),
          canvasTop: canvasRect.top - containerRect.top,
          user: user,
          index: index,
        });
      }
    });

    setAvatarPositions(positions);
  }, [isMobile, safeUsers]);

  // const handleImageError = (
  //   e: React.SyntheticEvent<HTMLImageElement>,
  // ): void => {
  //   const target = e.target as HTMLImageElement;
  //   target.style.display = "none";
  //   const nextSibling = target.nextSibling as HTMLElement;
  //   if (nextSibling) {
  //     nextSibling.style.display = "flex";
  //   }
  // };

  useEffect(() => {
    // Early return if no users data
    if (!canvasRef.current || safeUsers.length === 0) return;

    // Destroy existing chart
    if (chartRef.current) {
      chartRef.current.destroy();
    }

    const ctx = canvasRef.current.getContext("2d");
    if (!ctx) return;

    const gradients = safeUsers.map((_, index) => {
      const g = ctx.createLinearGradient(0, 0, 0, 400);

      if (index === 0) {
        g.addColorStop(0, "#617B61");
        g.addColorStop(1, "#427A43");
      } else if (index === 1) {
        g.addColorStop(0, "#C0BCA1");
        g.addColorStop(1, "#C0B87A");
      } else if (index === 2) {
        g.addColorStop(0, "#F1C242");
        g.addColorStop(1, "#F2E3BB");
      } else {
        g.addColorStop(0, "#2caaff");
        g.addColorStop(1, "#B8DAF4");
      }

      return g;
    });

    chartRef.current = new Chart.Chart(ctx, {
      type: "bar",
      data: {
        labels: safeUsers.map(() => ""),
        datasets: [
          {
            label: type === "rides" ? "Courses" : "Reservations",
            data:
              type === "rides"
                ? safeUsers.map((user) => Number(user.ridesCount))
                : safeUsers.map((user) => Number(user.bookingsCount)),
            backgroundColor: gradients,
            borderColor: barColors.map((color) => color.solid),
            borderWidth: 0,
            borderRadius: 30,
            borderSkipped: false,
            barThickness: isMobile ? 35 : 70,
            categoryPercentage: 0.5,
            barPercentage: 0.9,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        layout: {
          padding: {
            top: 50,
            bottom: 10,
            left: 5,
            right: 5,
          },
        },
        plugins: {
          legend: {
            display: false,
          },
          datalabels: {
            color: "#000000",
            anchor: "center",
            align: "center",
            font: {
              weight: "bold",
              size: 12,
            },
            formatter: (value: number) => value,
          },
          tooltip: {
            backgroundColor: "rgba(17, 24, 39, 0.95)",
            titleColor: "white",
            bodyColor: "white",
            borderColor: "rgba(255, 255, 255, 0.1)",
            borderWidth: 1,
            cornerRadius: 12,
            bodySpacing: 5,
            displayColors: false,
            titleFont: {
              size: 12,
              weight: "bold",
            },
            bodyFont: {
              size: 11,
            },
            padding: 10,
            position: "average",
            yAlign: "bottom",
            xAlign: "center",

            callbacks: {
              title: function (context: ChartTooltipItem[]): string {
                const index = context[0].dataIndex;
                return `🏆 ${safeUsers[index].fullName}`;
              },
              label: function (context: TooltipContext): string[] {
                const index = context.dataIndex;
                const user = safeUsers[index];
                return [
                  `🚗 ${user.ridesCount} courses terminées`,
                  `📅 ${user.bookingsCount} reservations confirmées`,
                  `💰 ${Number(user.totalPaid).toLocaleString()} DH total payés`,
                  `⭐ ${user.rating}/5.0 étoiles`,
                  `🥇 Position: ${index + 1}${index === 0 ? "ère" : "ème"}`,
                ];
              },
            },
          },
        },
        scales: {
          x: {
            display: false,
            grid: {
              display: false,
            },
          },
          y: {
            beginAtZero: true,
            grid: {
              color: "rgba(156, 163, 175, 0.2)",
              lineWidth: 1,
            },
            ticks: {
              color: "#6B7280",
              font: {
                size: 10,
              },
              padding: 5,
              callback: function (value: any): string {
                return value.toString();
              },
            },
          },
        },
        animation: {
          duration: 100,
          easing: "easeOutQuart",
          onComplete: () => {
            setTimeout(updateAvatarPositions, 100);
          },
        },
        onResize: () => {
          setTimeout(updateAvatarPositions, 100);
        },
        interaction: {
          intersect: false,
          mode: "index",
        },
      },
    });

    // Initial position update
    setTimeout(updateAvatarPositions, 100);

    // Cleanup function
    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
      }
    };
  }, [safeUsers, barColors, updateAvatarPositions, type, isMobile]);

  return (
    <div
      className={`${isMobile ? "p-0" : "p-3"} container mx-auto sm:p-6 lg:p-8 bg-gradient-to-br from-white to-gray-50 rounded-xl sm:rounded-2xl shadow-xl sm:shadow-2xl`}
    >
      {/* Chart Container with Floating Avatars */}
      <div ref={containerRef} className="relative mb-4 sm:mb-6 lg:mb-8">
        <div className="relative h-48 sm:h-64 lg:h-80">
          <canvas ref={canvasRef} className="w-full h-full" />

          {/* Floating Avatars */}
          {avatarPositions.map((pos, index) => (
            <div
              key={index}
              className="absolute transition-all duration-300 hover:scale-110 cursor-pointer"
              style={{
                left: `${pos.left}px`,
                top: `${pos.top + pos.canvasTop}px`,
                zIndex: 20,
                marginTop: "40px",
              }}
            >
              {/* Avatar Container */}
              <div className="relative group">
                <div
                  className="relative rounded-full p-1 shadow-lg sm:shadow-xl"
                  style={{
                    width: isMobile ? "40px" : "60px",
                    height: isMobile ? "40px" : "60px",
                    zIndex: 2,
                  }}
                >
                  <div className="w-full h-full rounded-full overflow-hidden ">
                    {pos.user?.profilePicture ? (
                      <Link
                        to={`/users/${pos.user?.id}`}
                        className="fw-bold m-0"
                      >
                        <img
                          src={pos.user?.profilePicture}
                          alt=""
                          width={"100%"}
                          height={"100%"}
                          style={{ borderRadius: "100%" }}
                        />
                      </Link>
                    ) : (
                      <Link
                        to={`/users/${pos.user?.id}`}
                        className="fw-bold m-0"
                      >
                        <User className="w-full h-full text-black" />
                      </Link>
                    )}
                  </div>
                </div>

                {/* Rank Badge  */}
                <div
                  className="absolute  w-3 h-3 sm:w-3.5 sm:h-3.5 bg-white rounded-full shadow-lg flex items-center justify-center border border-gray-100 "
                  style={{
                    top: "-15px",
                    right: "-20%",
                    transform: "translateX(-50%)",
                    zIndex: 3,
                  }}
                >
                  {getRankIcon(pos.index) || null}
                </div>

                {/* Rating Badge - Positioned below avatar */}
                <div
                  className={`absolute flex items-center gap-2 text-white ${isMobile ? "px-2" : "px-2"}  py-0.5 rounded-pill shadow-lg `}
                  style={{
                    fontSize: isMobile ? "8px" : "16px",
                    backgroundColor: "#f97722",
                    zIndex: 3,
                    minWidth: isMobile ? "30px" : "40px",
                    whiteSpace: "nowrap",
                  }}
                >
                  <Star
                    className="fill-current flex-shrink-0"
                    style={{
                      width: isMobile ? "14px" : "20px",
                      height: isMobile ? "14px" : "20px",
                    }}
                  />
                  <span>{pos.user.rating}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default TopUsersChart;
